import { useState, useEffect, useRef } from 'react';
import { Drawer, Search, DrawerOpenAt, MLPVersion, useTranslation } from '@nextgen-web-framework/all';
import { DynamicObjectInterface } from '@commerce-profile-webcomponents/sdk';
import { SearchDrawerProps } from './types/propTypes';
import styles from './SearchDrawer.module.scss';

const SearchDrawer = ({
  title,
  fieldName,
  data,
  config,
  onClose,
  callback,
  selectedValue,
  isLoading,
}: SearchDrawerProps) => {
  const t = useTranslation();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [results, setResults] = useState<DynamicObjectInterface>();
  const inputRef = useRef(null);

  useEffect(() => {
    const sortedData = data.sort(sortByLabel);
    setResults(sortedData);
  }, [data]);

  const sortByLabel = (a: DynamicObjectInterface, b: DynamicObjectInterface) =>
    a[config?.label].localeCompare(b[config?.label]);

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);

    const filteredResults = data
      .filter(
        (item: DynamicObjectInterface) =>
          item[config?.label].toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()) === true,
      )
      .sort(sortByLabel);

    setResults(filteredResults);
  };

  const onItemClick = (item: DynamicObjectInterface) => {
    callback?.(fieldName, item);
    setResults([]);
    setSearchKeyword('');
    onClose?.(fieldName);
  };

  const onClearHandler = () => {
    setSearchKeyword('');
    setResults(data);
  };

  return (
    <Drawer
      header={`${title}`}
      closeIcon
      onClose={() => onClose?.(fieldName)}
      bottomGap={0}
      useReactPortal={true}
      portalContainerId={'react-portal-drawer'}
      drawerOpenAt={DrawerOpenAt.AVAILABLE_HEIGHT}
      subHeader={
        <div className={styles['search-box']}>
          <Search
            mlpVersion={MLPVersion.TWO}
            onChange={searchOnChange}
            value={searchKeyword}
            onSearch={() => null}
            onClear={onClearHandler}
            ref={inputRef}
          />
        </div>
      }
    >
      <div
        className={styles['address-search-drawer']}
        id="address-search-drawer-id"
        data-testid="address-search-drawer-testid"
      >
        <div className={styles['search-results']}>
          {results?.map((item: DynamicObjectInterface) => {
            let selectedClass = '';

            if (selectedValue === item?.[config?.key]) {
              selectedClass = styles['selected'];
            }

            return (
              <div
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onItemClick(item);
                }}
                tabIndex={-1}
                aria-hidden="true"
                data-testId="search-list"
                className={`${styles['search-item']} ${selectedClass}`}
                key={item.code}
              >
                {item[config.label]}
              </div>
            );
          })}
          {results?.length === 0 && !isLoading && <>{t('profile.common.noRecord')}</>}
          {isLoading && <>{t('web.loader.title')}</>}
        </div>
      </div>
    </Drawer>
  );
};

export default SearchDrawer;
